import { CalendarColumnCell } from "@tento-ui/components/calendar/calendar-column-cell";
import type { CalendarSubcolumnSpan } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { EventId, type Option } from "@tento-lona/sheets";
import { DateFragment, DateTime, TimePoint } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";
import type { FrameMarks } from "@tento-ui/frame-marks";
import type { EventIdPromotions } from "@tento-lona/platform";
import { isMovableItem } from "@tento-lona/sheets-ui/sheet/gestures/interactable-item.ts";

/**
 * A days strip's span cells (F11): which spans are visible in a layer, the
 * per-layer cell pool (take, bind, reveal, reconcile, pool), and the layer's
 * read-failure marker. Pure DOM helpers over one layer; the strip
 * (calendar-days-strip.ts) owns when they run.
 */

/** A span and its time truncated to the strip's window. */
export type VisibleSpan = {
  span: CalendarSubcolumnSpan;
  time: TimePoint.Range;
};

export const POOLED_SPAN_CELL = "data-span-pooled";

/** The DOM surface a span cell's bind writes: `HTMLElement` satisfies it; tests count through it. */
export interface SpanCellSurface {
  id: string;
  /** `boolean | string`: Deno's DOM lib types `HTMLElement.hidden` so (`"until-found"`); the binds write booleans. */
  hidden: boolean | string;
  inert: boolean;
  readonly style: {
    opacity: string;
    pointerEvents: string;
    borderRadius: string;
    background: string;
    getPropertyValue(name: string): string;
    setProperty(name: string, value: string): void;
  };
  hasAttribute(name: string): boolean;
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
  toggleAttribute(name: string, force: boolean): boolean;
}

/**
 * The span-cell writes that diff against the inline style, as `assignStyles`
 * does (N5-1): `pointer-events` and the `--default-interactable` custom
 * property. Every other span-cell write is a plain assignment (the
 * quiet-span-writes arm, H-B, lost its A/B in W9 and was removed).
 */
export class SpanCellWrites {
  /**
   * Always diffed (N5-1): before H-B this write went through `assignStyles`,
   * which diffs against the inline style.
   */
  static pointerEvents($cell: SpanCellSurface, value: string): void {
    if ($cell.style.pointerEvents !== value) $cell.style.pointerEvents = value;
  }
  /** Always diffed (N5-1), as `assignStyles` did for custom properties. */
  static property($cell: SpanCellSurface, name: string, value: string): void {
    if ($cell.style.getPropertyValue(name) !== value) $cell.style.setProperty(name, value);
  }
}

/** Reveal a pooled or new cell under `id` (the writes `bindSpanCell` starts with). */
export function revealSpanCellSurface($cell: SpanCellSurface, id: string): void {
  $cell.id = id;
  $cell.hidden = false;
  $cell.inert = false;
  $cell.style.opacity = "";
  $cell.removeAttribute(POOLED_SPAN_CELL);
}

/** Park a cell in the pool, hidden and inert. */
export function poolSpanCellSurface($cell: SpanCellSurface): void {
  $cell.hidden = true;
  $cell.inert = true;
  $cell.style.opacity = "0";
  SpanCellWrites.pointerEvents($cell, "none");
  $cell.setAttribute(POOLED_SPAN_CELL, "true");
}

/**
 * The cell-level writes of a span bind after the reveal: the editable
 * marker, the item-less chrome reset and the interactable pointer style.
 * (Content writes are `span.bindContent`'s.)
 */
export function styleSpanCellSurface($cell: SpanCellSurface, editable: boolean, hasItem: boolean, pointerEvents: string): void {
  $cell.toggleAttribute("editable", editable);
  $cell.style.borderRadius = hasItem ? "" : "unset";
  $cell.style.background = hasItem ? "" : "unset";
  SpanCellWrites.property($cell, "--default-interactable", pointerEvents);
  SpanCellWrites.pointerEvents($cell, "var(--default-interactable)");
}

/** A span layer still in the strip (a recycled column's layer has left the DOM). */
export function isLiveLayer($layout: HTMLElement): boolean {
  return $layout.isConnected;
}

/**
 * On a span layer whose latest provider read rejected (styled by
 * `CalendarColumnLayoutGroup`); its next successful paint removes it.
 */
const READ_FAILED_LAYER = "data-read-failed";
const READ_FAILED_TITLE = "Some events could not be loaded";

export function markLayerReadFailed($layout: HTMLElement, failed: boolean): void {
  if ($layout.hasAttribute(READ_FAILED_LAYER) === failed) return;
  $layout.toggleAttribute(READ_FAILED_LAYER, failed);
  if (failed) $layout.setAttribute("title", READ_FAILED_TITLE);
  else $layout.removeAttribute("title");
}

/** Span id for the floating drag-ghost preview — distinct from the dragged
 *  event's own id so the original cell and the ghost cell can both render at
 *  once (the original stays put during the drag). The `__dragghost__` prefix cannot occur in a real event id, so the pooled cells never collide. */
export function dragGhostSpanId(eventId: string): string {
  return `__dragghost__${eventId}`;
}

export function visibleSpanEntries(
  spans: readonly CalendarSubcolumnSpan[],
  windowed: DateFragment.Windowed,
): VisibleSpan[] {
  const visible: VisibleSpan[] = [];
  for (const span of spans) {
    const truncatedTime = windowed.truncate(
      new DateTime.Range(span.start, span.end ?? span.start),
    );
    if (!truncatedTime) continue;
    visible.push({
      span,
      time: TimePoint.Range.fromTodRange(truncatedTime),
    });
  }
  return visible;
}

export function spanCells(
  $layout: HTMLElement,
): CalendarColumnCell<HTMLElement>[] {
  return [...$layout.children].filter(
    ($child): $child is CalendarColumnCell<HTMLElement> =>
      $child instanceof CalendarColumnCell,
  );
}

export function spanCellsById(
  $layout: HTMLElement,
): Map<string, CalendarColumnCell<HTMLElement>> {
  const byId = new Map<string, CalendarColumnCell<HTMLElement>>();
  for (const $cell of spanCells($layout)) {
    if ($cell.id === "") continue;
    const existing = byId.get($cell.id);
    if (!existing || existing.hasAttribute(POOLED_SPAN_CELL)) {
      byId.set($cell.id, $cell);
    }
  }
  return byId;
}

export function spanCellPool(
  $layout: HTMLElement,
  visible: readonly VisibleSpan[],
  reclaimRetired: boolean,
  promotions: Option<EventIdPromotions.Surface>,
): CalendarColumnCell<HTMLElement>[] {
  // Reserve the entire incoming set before serving the first new event.
  // Otherwise an earlier new ID could consume a later live cell or the
  // optimistic source that a later server ID needs to promote in place.
  const reserved = new Set(visible.map(({ span }) => span.id));
  for (const { span } of visible) {
    const eventId = EventId.parse(span.id);
    const sourceId = eventId == null ? null : promotions?.sourceIdFor(eventId) ?? null;
    if (sourceId != null) reserved.add(sourceId);
  }
  return spanCells($layout).filter(($cell) =>
    !reserved.has($cell.id) &&
    (reclaimRetired || $cell.hasAttribute(POOLED_SPAN_CELL))
  );
}

export function takeSpanCell(
  id: string,
  existing: Map<string, CalendarColumnCell<HTMLElement>>,
  pool: CalendarColumnCell<HTMLElement>[],
  /** The client's event-id promotions; `null` when the row renders no
   *  client's events (no promotion to resolve). */
  promotions: Option<EventIdPromotions.Surface>,
): Option<CalendarColumnCell<HTMLElement>> {
  const sameKey = existing.get(id);
  if (sameKey) {
    const idx = pool.indexOf(sameKey);
    if (idx >= 0) pool.splice(idx, 1);
    return sameKey;
  }
  // Ghost promotion: a created event's optimistic→server id re-key is a
  // RENAME of the live ghost cell, never a teardown (pooling the ghost while
  // its replacement waits behind `pending-layout` can blank a frame — the
  // create "blink"). `bindSpanCell` re-keys the returned cell to `id`, so
  // reusing the ghost's element here IS the in-place promotion; being reused,
  // it also skips the `pending-layout` first-paint hide.
  const sourceId = promotions?.sourceIdFor(EventId.of(id)) ?? null;
  if (sourceId != null) {
    promotions?.consume(EventId.of(id));
    const ghost = existing.get(sourceId);
    if (ghost) {
      const idx = pool.indexOf(ghost);
      if (idx >= 0) pool.splice(idx, 1);
      Tracing.log("event/dom-promote-reused", { from: sourceId, to: id });
      return ghost;
    }
    Tracing.log("event/dom-promote-miss", { to: id });
  }
  const $cell = pool.shift() ?? null;
  // A retiring live cell keeps its DOM slot, but its old geometry cannot be
  // shown under the new identity. Mark it pooled so the strip holds it
  // `pending-layout` until layout places the new content; bindSpanCell
  // reveals it in the same task, so hiding it here (hidden, inert, opacity,
  // pointer-events) would flip each twice with no frame in between. Active
  // drags only supply old spares.
  if ($cell && !$cell.hasAttribute(POOLED_SPAN_CELL)) $cell.setAttribute(POOLED_SPAN_CELL, "true");
  return $cell;
}

function revealSpanCell(
  $cell: CalendarColumnCell<HTMLElement>,
  id: string,
): void {
  revealSpanCellSurface($cell, id);
}

/**
 * Bind one span cell. `phases` (the paint's `PAINT_PHASES`, frame marks):
 * the chip's content bind is split out as phase 1 and the cell's own bind
 * before it as phase 0.
 */
export function bindSpanCell(
  $cell: CalendarColumnCell<HTMLElement>,
  span: CalendarSubcolumnSpan,
  time: TimePoint.Range,
  phases: Option<FrameMarks.Phases> = null,
): HTMLElement {
  revealSpanCell($cell, span.id);
  // A dragged span floats (excluded from lane collision) so it tracks the
  // cursor without reshuffling its neighbours.
  $cell.bind({ id: span.id, time, ignoreLayout: span.isDragging ?? false });
  // Editable items surface the top/bottom resize handles (ns-resize) on hover.
  const spanItem = span.item ?? null;
  const editable = isMovableItem(spanItem) && spanItem.isEditable;

  const existingContent = $cell.firstElementChild instanceof HTMLElement
    ? $cell.firstElementChild
    : null;
  phases?.split(0);
  const $span = span.bindContent(existingContent);
  $span.setAttribute("start", span.start.toString());
  $span.setAttribute("end", span.end?.toString() ?? "");
  if ($cell.firstElementChild !== $span) {
    $span.slot = "content";
    $cell.replaceChildren($span);
  }
  phases?.split(1);

  styleSpanCellSurface($cell, editable, span.item != null, span.pointerEvents ?? "");
  span.applyToCell?.($cell, span);
  return $span;
}

export function reconcileSpanCells(
  $layout: HTMLElement,
  rendered: CalendarColumnCell<HTMLElement>[],
  used: Set<CalendarColumnCell<HTMLElement>>,
): void {
  const cells = spanCells($layout);
  for (const $cell of cells) {
    if (!used.has($cell)) poolSpanCell($cell);
  }
  // Binding and lane layout still run. Avoid detaching settled subtrees only
  // when the complete order already matches: rendered cells, then spares.
  if (
    $layout.childNodes.length === cells.length &&
    rendered.length <= cells.length &&
    cells.every(($cell, index) =>
      index < rendered.length
        ? $cell === rendered[index]
        : !used.has($cell)
    )
  ) return;

  for (const $cell of rendered) $layout.appendChild($cell);
  for (const $cell of cells) {
    if (!used.has($cell)) $layout.appendChild($cell);
  }
}

export function poolSpanCell($cell: CalendarColumnCell<HTMLElement>): void {
  poolSpanCellSurface($cell);
}
